import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import helpers from "helpers/index";
import { TypedUser } from "interface/user.model";
import queryClient, { IQueryParams } from 'queries';
import { TypedJourney } from "./journey.query";
import { TypedCustomer } from "interface/customer.model";
import { TypedTicket } from "./ticket.query";
import { TypedTransactions } from "./transactions.query";

type TripManifestPerson = {
    user_id: bigint;
    user_address: string | null;
    display_name: string | null;
    user_phonenumber: string | null;
    user_avatar: string | null;
};

type DriverLocation = {
    lat: number;
    lng: number;
    createdAt: bigint;
};

type TripManifestDriver = TripManifestPerson & {
    drive_location: DriverLocation | null;
};

type TripManifestPeopleResult = {
    drivers: TripManifestDriver[];
    pickup: TripManifestPerson[];
    dropoff: TripManifestPerson[];
};


export enum ENUM_TRIP_FEE {
    FUEL_FEE = 'FUEL_FEE',
    PARKING_FEE = 'PARKING_FEE',
    REPAIR_MAINTENANCE_FEE = 'REPAIR_MAINTENANCE_FEE',
    TOLL_FEE = 'TOLL_FEE',
    RELAY_TRANSFER_FEE = 'RELAY_TRANSFER_FEE',      // thuê Grab/xe ôm giao – nhận hộ
    OTHER_FEE = 'OTHER_FEE',
}


export enum ENUM_ORDER_PAYMENT_STATUS {
    PAID = 1,
    UNPAY = 0,
}


export const TRIP_FEE_LABELS: Record<ENUM_TRIP_FEE, string> = {
    [ENUM_TRIP_FEE.FUEL_FEE]: 'Đổ nhiên liệu',
    [ENUM_TRIP_FEE.PARKING_FEE]: 'Vé gửi xe',
    [ENUM_TRIP_FEE.REPAIR_MAINTENANCE_FEE]: 'Sửa xe/Bảo dưỡng',
    [ENUM_TRIP_FEE.TOLL_FEE]: 'Phí cao tốc/Trạm thu phí',
    [ENUM_TRIP_FEE.RELAY_TRANSFER_FEE]: 'Trung chuyển/Thuê ngoài (Grab/xe ôm)',
    [ENUM_TRIP_FEE.OTHER_FEE]: 'Phí khác',
};


export interface Typedorder_invoice_info {
    id?: string;
    order_id?: string;
    tax_type?: string; /**  enum('PERSONAL', 'COMPANY')  */
    name?: string;
    buyer_name?: string;
    tax_code?: string;
    tax_cccd?: string;
    invoice_email?: string;
    invoice_phone?: string;
    invoice_address?: string;
    payment_method?: string; /**  enum('CASH', 'TRANSFER')  */
    is_customer_required?: number; /** Người dùng yêu cầu à? */
    createdAt?: string;


    invoice_save_to_invoice_list?: number; /** Tùy chọn lưu lại xuất cho lần sau ... */
}



export enum ENUM_ORDER_FEE {
    PICKUP_FEE = 'PICKUP_FEE',
    DROPOFF_FEE = 'DROPOFF_FEE',
    PARKING_FEE = 'PARKING_FEE',
    LUGGAGE_FEE = 'LUGGAGE_FEE',
    TOLL_FEE = 'TOLL_FEE',
    OTHER_FEE = 'OTHER_FEE',
}


// Map enum -> label (tên hiển thị)
export const ORDER_FEE_LABEL: Record<ENUM_ORDER_FEE, string> = {
    [ENUM_ORDER_FEE.PICKUP_FEE]: 'Phí đón',
    [ENUM_ORDER_FEE.DROPOFF_FEE]: 'Phí trả',
    [ENUM_ORDER_FEE.PARKING_FEE]: 'Phí chờ',
    [ENUM_ORDER_FEE.TOLL_FEE]: 'Phí cao tốc',
    [ENUM_ORDER_FEE.LUGGAGE_FEE]: 'Phí hành lý',
    [ENUM_ORDER_FEE.OTHER_FEE]: 'Phí khác',
};

export interface PickupMeta {
    place_id: string,
    extra_price: number,
    minute: number,
    note: string
}

export interface TypedOrders {
    id?: string;
    order_code?: string;
    departure_date?: number | string;
    pickup_time?: string;
    pickup_address?: string;

    dropoff_address?: string,


    dropoff_contact_phonenumber?: string;
    dropoff_contact_name?: string;


    customer_id?: string;
    customer_name?: string;
    customer_phone?: string;

    journey_id?: string;
    trip_id?: string;
    total_fee?: number;
    total_must_pay?: number;
    total_seat?: number;
    payment_status?: number;
    note?: string;
    order_status?: number;
    createdBy?: string;
    createdAt?: string;

    is_exported_invoice?: number; /* mặc định 0 là chưa xuất hóa đơn, 1 là đã xuất hóa đơn */
    invoiceExportedAt?: string;


    author_data?: TypedUser;
    customer_data?: TypedCustomer;
    journey?: TypedJourney
    ticket?: TypedTicket[];

    /** Cho phep put một vài thông tin khác khi book vé */
    trip_manifest_id?: any;

    /** Để đặt vé */
    price_table?: {
        price?: number,
        discount?: number,
        ticket_type?: string,
        seat_position?: string,
        /** dành cho update */
        ticket_id?: bigint,
    }[]

    /** Để lưu vào meta */
    /** Chỉ là để lưu vào user Address book */
    user_address_book_meta_pickup?: PickupMeta,
    user_address_book_meta_dropoff?: PickupMeta,

    order_invoice_info?: Typedorder_invoice_info

    order_to_invoice?: {
        invoices?: {
            invoice_id: string,
            is_signed: number,
            shDon: string,
            maTraCuu: string,
            invoice_status: number,
        }
    }[]


    /** Để đặt vé */
    order_fee?:
    {
        fee_description: string,
        fee_value: number,
    }[],

    transactions?: TypedTransactions
}

export const OrderStatus: { [key: number]: string } = {
    0: 'Nháp',
    1: 'Đã xếp chuyến',
    2: 'Trong chuyến',
    3: 'Hoàn thành',
    9: 'Hủy',
};


type IQuery = TypedOrders & IQueryParams;

/*
* List of all Orders
*/
export function useGetOrders(object: IQuery) {
    const EndURL = helpers.buildEndUrl(object);
    return useQuery({
        queryKey: ["orders/fetch_entity_list", object],
        queryFn: () => axios.get<TypedOrders[]>(`/orders/${EndURL}`).then(response => {
            let { data, headers } = response;
            return {
                entities: data,
                totalItems: Number(headers['x-total-count'] || 0)
            }
        }),
        retry: 1,
        enabled: !!object
    });
}


export function useMutateSearchOrders() {
    return useMutation({
        mutationKey: ["orders/quick_search"],
        mutationFn: (object: IQuery) => axios.get<TypedOrders[]>(`/orders/quicksearch/${helpers.buildEndUrl(object)}`).then(response => response.data),
        retry: 1,
    });
}

/** Admin thanh toán cho vé ... */
export function useMutateOrderPaid() {
    return useMutation({
        mutationKey: ['orders/admin_paid'],
        mutationFn: (payload: {
            casher: bigint,
            payment_method: string,
            description: string,
            order_id: bigint,
        }) => axios.post(`/orders/paid`, payload).then((response) => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });
        }
    });
}

export function useMutateGetOrderViaCode() {
    return useMutation({
        mutationKey: ["orders/fetch_entity"],
        mutationFn: (order_code: string) => axios.get<TypedOrders>(`/orders/get_via_code/${order_code}`).then(response => response.data),
        retry: 1,
    });
}



export function useOrderClearTransaction() {
    return useMutation({
        mutationKey: ["orders/clear_order_transaction"],
        mutationFn: (order_id: string | bigint) => axios.delete<any>(`/orders/clear_transaction/${order_id}`).then(response => response.data),
        retry: 1,
    });
}

/*
* List of all Orders
*/
export function useGetOrderMaps(trip_manifest_id: bigint | string) {
    return useQuery({
        queryKey: ["orders/fetch_map_list", trip_manifest_id],
        queryFn: () => axios.get<TripManifestPeopleResult>(`/orders/maps/${trip_manifest_id}`).then(response => response.data),
        retry: 1,
        enabled: !!trip_manifest_id
    });
}


export function useGetOrdersInManifest() {
    return useMutation({
        mutationKey: ["orders/fetch_list_order_in_manifest"],
        mutationFn: (manifest_id: string | bigint) => axios.get<TypedOrders[]>(`/orders/manifest_list/${manifest_id}`).then(response => response.data),
        retry: 1,
    });
}

export function useGetOrder(id: string) {
    return useQuery({
        queryKey: ["orders/fetch_entity", id],
        queryFn: () => axios.get<TypedOrders>(`/orders/${id}`).then(response => response.data),
        retry: 1,
        refetchOnMount: true,
        enabled: !!id
    });
}

export function useGetTaxFillOrder(order_code: string) {
    return useQuery({
        queryKey: ["taxfill/fetch_order", order_code],
        queryFn: () => axios.get<{ valid: boolean, order_data: TypedOrders, note: string }>(`/taxfill/order/${order_code}`).then(response => response.data),
        retry: 1,
        refetchOnMount: true,
        enabled: !!order_code
    });
}


export function useMutateGetOrders() {
    return useMutation({
        mutationKey: ["orders/fetch_list_order"],
        mutationFn: (object: IQuery) => axios.get<TypedOrders[]>(`/orders/${helpers.buildEndUrl(object)}`).then(response => response.data),
        retry: 1,
    });
}

export function useMutateUpdateOrder() {
    return useMutation({
        mutationKey: ["orders/fetch_list_order"],
        mutationFn: ({ id, ...rest }: TypedOrders) => axios.patch<TypedOrders>(`/orders/${id}`, rest).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['orders/fetch_entity_list'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest_ticket/fetch_entity_list'] }); // reset lại cái danh sách vé để hiển thị

        },
        retry: 1,
    });
}


/** Admin book vé ... */
export function useMutateOrderBooking() {
    return useMutation({
        mutationKey: ['orders/admin_booking'],
        mutationFn: (body: TypedOrders) => axios.post<TypedOrders[]>(`/orders`, body).then((response) => response.data),
        onSuccess: () => {
            /** Load lại trip */
            queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest_ticket/fetch_entity_list'] });
            // queryClient.invalidateQueries({ queryKey: ['orders/fetch_entity_list'] });
            // queryClient.invalidateQueries({ queryKey: ['orders/fetch_list_order'] });
        }
    });
}

/** Admin hủy vé ... */
export function useMutateOrderCancel() {
    return useMutation({
        mutationKey: ['orders/admin_cancel'],
        mutationFn: (order_id: number | string) => axios.delete(`/orders/${order_id}`).then((response) => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity_list'] });
            // queryClient.invalidateQueries({ queryKey: ['orders/fetch_entity_list'] });
            // queryClient.invalidateQueries({ queryKey: ['orders/fetch_list_order'] });

        }
    });
}




/** Cập nhật thông tin thuế của đơn hàng, trả về 201 updated ... */
export function useMutateOrderTaxInformation() {
    return useMutation({
        mutationKey: ['orders/order_invoice_info_update'],
        mutationFn: (data: Typedorder_invoice_info) => axios.patch<void>(`/order_invoice_info/${data.order_id}`, data).then((response) => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });

            queryClient.invalidateQueries({ queryKey: ['orders/fetch_entity'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest_ticket/fetch_entity_list'] }); // reset lại cái danh sách vé để hiển thị

        }
    });
}


/** Xóa bỏ thông tin thuế */
export function useMutateDeleteOrderTaxInformation() {
    return useMutation({
        mutationKey: ['orders/delete_order_invoice_info'],
        mutationFn: (order_id: any) => axios.delete<void>(`/order_invoice_info/${order_id}`).then((response) => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });

            queryClient.invalidateQueries({ queryKey: ['orders/fetch_entity'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest_ticket/fetch_entity_list'] }); // reset lại cái danh sách vé để hiển thị

        }
    });
}


/** Cập nhật thông tin thuế cho màn tax-fill public */
export function useMutateTaxFillOrderTaxInformation() {
    return useMutation({
        mutationKey: ['taxfill/order_invoice_info_update'],
        mutationFn: (data: Typedorder_invoice_info) => axios.put<void>(`/taxfill/order_invoice_info/${data.order_id}`, data).then((response) => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['taxfill/fetch_order'] });
        }
    });
}
